import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus,
  X,
  Sparkles,
  Popcorn,
  Medal,
  Flower2,
  Flag,
  Music,
  Gamepad2,
  PiggyBank,
  Utensils,
  Home,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HASHTAG_CATEGORY_OPTIONS } from '@/pages/reports/components/ui/TiktokTrends';

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Sparkles,
  Popcorn,
  Medal,
  Flower2,
  Flag,
  Music,
  Gamepad2,
  PiggyBank,
  Utensils,
  Home,
};

export interface HashtagItem {
  text: string;
  link?: string;
}

export interface HashtagCategoryItem {
  categoryId: string;
  hashtags: HashtagItem[];
}

function normalizeHashtags(
  hashtags?: string[] | { text?: string; link?: string }[]
): HashtagItem[] {
  if (!Array.isArray(hashtags)) return [];
  return hashtags
    .map((tag) => {
      if (typeof tag === 'string') {
        const text = tag.trim().replace(/^#/, '');
        return text ? { text } : null;
      }
      // Keep empty rows while editing; they are filtered only at render time.
      const text = (tag.text ?? '').replace(/^#/, '');
      const link = (tag.link ?? '').trim();
      return { text, ...(link ? { link } : {}) };
    })
    .filter((tag): tag is HashtagItem => tag !== null);
}

/** Legacy shape from older reports (name + color); normalized to categoryId when possible */
function normalizeItem(item: { name?: string; color?: string; categoryId?: string; hashtags?: string[] | { text?: string; link?: string }[] }): HashtagCategoryItem {
  if (item.categoryId && typeof item.hashtags !== 'undefined') {
    return { categoryId: item.categoryId, hashtags: normalizeHashtags(item.hashtags) };
  }
  const name = (item.name || '').trim();
  const match = HASHTAG_CATEGORY_OPTIONS.find((o) => o.label.toLowerCase() === name.toLowerCase());
  return {
    categoryId: match?.id ?? 'geral',
    hashtags: normalizeHashtags(item.hashtags),
  };
}

interface HashtagCategoriesInputProps {
  value?: HashtagCategoryItem[] | { name?: string; color?: string; hashtags?: string[] | { text?: string; link?: string }[] }[];
  onChange: (_value: HashtagCategoryItem[]) => void;
}

export const HashtagCategoriesInput: React.FC<HashtagCategoriesInputProps> = ({
  value = [],
  onChange,
}) => {
  const normalized = useMemo(() => (Array.isArray(value) ? value.map(normalizeItem) : []), [value]);
  const [items, setItems] = useState<HashtagCategoryItem[]>(normalized);

  useEffect(() => {
    setItems(normalized);
  }, [normalized]);

  const add = () => {
    const next = [...items, { categoryId: HASHTAG_CATEGORY_OPTIONS[0].id, hashtags: [] }];
    setItems(next);
    onChange(next);
  };

  const remove = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    setItems(next);
    onChange(next);
  };

  const updateCategoryId = (index: number, categoryId: string) => {
    const next = items.map((item, i) => (i === index ? { ...item, categoryId } : item));
    setItems(next);
    onChange(next);
  };

  const addHashtag = (index: number) => {
    const next = items.map((item, i) =>
      i === index ? { ...item, hashtags: [...item.hashtags, { text: '', link: '' }] } : item
    );
    setItems(next);
    onChange(next);
  };

  const removeHashtag = (categoryIndex: number, hashtagIndex: number) => {
    const next = items.map((item, i) =>
      i === categoryIndex
        ? { ...item, hashtags: item.hashtags.filter((_, hIdx) => hIdx !== hashtagIndex) }
        : item
    );
    setItems(next);
    onChange(next);
  };

  const updateHashtagText = (categoryIndex: number, hashtagIndex: number, text: string) => {
    const next = items.map((item, i) =>
      i === categoryIndex
        ? {
            ...item,
            hashtags: item.hashtags.map((tag, hIdx) =>
              hIdx === hashtagIndex
                ? { ...tag, text: text.replace(/^#/, '') }
                : tag
            ),
          }
        : item
    );
    setItems(next);
    onChange(next);
  };

  const updateHashtagLink = (categoryIndex: number, hashtagIndex: number, link: string) => {
    const next = items.map((item, i) =>
      i === categoryIndex
        ? {
            ...item,
            hashtags: item.hashtags.map((tag, hIdx) =>
              hIdx === hashtagIndex
                ? { ...tag, link }
                : tag
            ),
          }
        : item
    );
    setItems(next);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button type="button" variant="outline" size="sm" onClick={add} className="gap-2">
          <Plus className="w-4 h-4" />
          Adicionar categoria
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Nenhuma categoria adicionada
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Categoria {index + 1}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => remove(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <Select value={item.categoryId} onValueChange={(v) => updateCategoryId(index, v)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {HASHTAG_CATEGORY_OPTIONS.map((opt) => {
                    const IconComponent = CATEGORY_ICONS[opt.icon];
                    return (
                      <SelectItem key={opt.id} value={opt.id}>
                        <span className="flex items-center gap-2">
                          {IconComponent && (
                            <IconComponent className="w-4 h-4 shrink-0 text-gray-600" />
                          )}
                          <span
                            className="inline-block w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: opt.color }}
                          />
                          {opt.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-600">Hashtags em alta</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => addHashtag(index)} className="gap-1.5">
                    <Plus className="w-3.5 h-3.5" />
                    Adicionar hashtag
                  </Button>
                </div>
                {item.hashtags.length === 0 ? (
                  <div className="text-xs text-gray-500 border border-dashed border-gray-200 rounded-md px-3 py-2">
                    Nenhuma hashtag adicionada
                  </div>
                ) : (
                  <div className="space-y-2">
                    {item.hashtags.map((tag, hashtagIndex) => (
                      <div key={hashtagIndex} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center">
                        <Input
                          placeholder="Hashtag (ex: bbb)"
                          value={tag.text}
                          onChange={(e) => updateHashtagText(index, hashtagIndex, e.target.value)}
                        />
                        <Input
                          type="url"
                          placeholder="Link opcional do TikTok"
                          value={tag.link ?? ''}
                          onChange={(e) => updateHashtagLink(index, hashtagIndex, e.target.value)}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeHashtag(index, hashtagIndex)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
